import React, { useEffect, useState, useMemo, useContext } from 'react';
import { Box, TextField, InputAdornment, Autocomplete } from '@mui/material';
import { BananaContext } from '@wikimedia/react.i18n';
import { Currency, getCurrencyInputRegex, decimalToIsoAmount, isoToDecimalAmount } from '../../../utils';

const AmountWithCurrency = ({ 
  value, 
  onChange, 
  getCurrencies,
  defaultCurrency = 'EUR',
  onBlur,
  containerProps,
  amountFieldProps = {},
  currencyFieldProps = {}
}) => {
  const translator = useContext(BananaContext);
  const [amount, setAmount] = useState(() => {
    if (value?.amount) {
      const currencyCode = value?.currency || defaultCurrency;
      try {
        // If the amount is already in ISO format, convert it back to decimal for display
        const decimalAmount = isoToDecimalAmount(value.amount, currencyCode);
        return decimalAmount.toString();
      } catch (error) {
        console.error('Error converting amount:', error);
        return '';
      }
    }
    return '';
  });
  
  const [currency, setCurrency] = useState(() => {
    if (value?.currency) {
      return { label: value.currency, value: value.currency };
    }
    return { label: defaultCurrency, value: defaultCurrency };
  });
  
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const inputRegex = useMemo(() => {
    try {
      return getCurrencyInputRegex(currency.value);
    } catch (error) {
      return /^\d*\.?\d{0,2}$/;
    }
  }, [currency.value]);

  // Format amount display according to currency precision
  const formatAmountDisplay = (value, currencyCode) => {
    if (!value) return '';
    
    try {
      const numericAmount = parseFloat(value);
      if (isNaN(numericAmount)) return value;
      
      const currencyObj = Currency.getUnsafe(currencyCode);
      return numericAmount.toFixed(currencyObj.exponent);
    } catch (error) {
      console.error('Error formatting amount:', error);
      return value;
    }
  };

  useEffect(() => {
    const fetchCurrencies = async () => {
      if (!getCurrencies) return;
      
      setLoading(true);
      try {
        const currencyList = await getCurrencies();
        setCurrencies(currencyList);
        // If initial currency is a string, convert it to object format
        if (typeof currency === 'string') {
          const currencyObj = currencyList.find(c => c.value === currency);
          if (currencyObj) {
            setCurrency(currencyObj);
          }
        }
      } catch (error) {
        console.error('Error fetching currencies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrencies();
  }, [getCurrencies]);

  const handleAmountChange = (event) => {
    const value = event.target.value;
    if (inputRegex.test(value) || value === '') {
      setAmount(value);

      if (onChange) {
        try {
          // Update parent component only if there's a valid amount
          if (value) {
            const numericAmount = parseFloat(value);
            if (!isNaN(numericAmount)) {
              const isoAmount = decimalToIsoAmount(numericAmount, currency.value);
              onChange({
                amount: isoAmount,
                currency: currency.value
              });
            }
          } else {
            // Clear the value if amount is empty
            onChange({
              amount: undefined,
              currency: currency.value
            });
          }
        } catch (error) {
          console.error('Error updating amount:', error);
        }
      }
    }
  };

  // Format the displayed amount when the currency changes
  useEffect(() => {
    if (amount) {
      setAmount(formatAmountDisplay(amount, currency.value));
    }
  }, [currency.value]);

  // Handle formatting amount on blur
  const handleBlur = (e) => {
    if (amount) {
      setAmount(formatAmountDisplay(amount, currency.value));
    }
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', ...containerProps?.sx }}>
      <TextField
        value={amount}
        onChange={handleAmountChange}
        onBlur={handleBlur}
        type="text"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Box sx={{ 
                minWidth: '70px', 
                display: 'flex', 
                alignItems: 'center',
                fontSize: '14px',
                color: 'text.secondary' 
              }}>
                {currency.value}
              </Box>
            </InputAdornment>
          ),
          ...amountFieldProps.InputProps
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '14px',
          },
          ...amountFieldProps.sx
        }}
        placeholder={translator.i18n('enter-exact-amount')}
        {...amountFieldProps}
      />
      
      <Autocomplete
        fullWidth
        size="small"
        loading={loading}
        options={currencies}
        value={currency}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => <TextField {...params} placeholder={translator.i18n('select-currency')} />}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        onChange={(_, newValue) => {
          if (newValue) {
            setCurrency(newValue);
            
            if (amount) {
              const currencyObj = Currency.getUnsafe(newValue.value);
              const parsedAmount = parseFloat(amount);
              if (!isNaN(parsedAmount)) {
                setAmount(parsedAmount.toFixed(currencyObj.exponent));
              }
            }

            if (onChange) {
              try {
                if (amount) {
                  const numericAmount = parseFloat(amount);
                  if (!isNaN(numericAmount)) {
                    const isoAmount = decimalToIsoAmount(numericAmount, newValue.value);
                    onChange({
                      amount: isoAmount,
                      currency: newValue.value
                    });
                  }
                } else {
                  onChange({
                    amount: undefined,
                    currency: newValue.value
                  });
                }
              } catch (error) {
                console.error('Error updating currency:', error);
              }
            }
          }
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            fontSize: '14px',
          },
          ...currencyFieldProps.sx
        }}
        {...currencyFieldProps}
      />
    </Box>
  );
};

export default AmountWithCurrency;